import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { type Board, type Clip } from '@air/api/types';
import { hydrateSelectedItemsAction } from '@air/redux-selected-items';

import { GalleryItemType } from '~/components/Gallery/types';
import { useViewsSelectAllStore } from '~/components/v2/store/features/views/useViewsSelectAllStore';
import { centralizedBoardIdSelector } from '~/store/centralizedBoard/selectors';
import { setSelectableGalleryItemsAction } from '~/store/selectedItems/actions';
import { type SelectableGalleryItem } from '~/store/selectedItems/types';

export function useGroupedGallerySelectedItems<B extends Board, C extends Clip>(
  boards: B[],
  assets: C[],
  files: C[],
  supportsPersistence = false,
) {
  const dispatch = useDispatch();
  const selectAll = useViewsSelectAllStore((state) => state.selectAll);
  const includeNestedItems = useViewsSelectAllStore((state) => state.includeNestedItems);
  const selectAllRootBoardId = useViewsSelectAllStore((state) => state.selectAllRootBoardId);
  const currentBoardId = useSelector(centralizedBoardIdSelector);

  //Send rendered items to reducer, to enable multiselect with SHIFT
  //Order of those is used to determine which items should be selected
  useEffect(() => {
    const selectableBoardItems: SelectableGalleryItem[] = boards.map((board) => ({
      id: board.id,
      type: GalleryItemType.board,
      item: board,
    }));

    const selectableAssetItems: SelectableGalleryItem[] = assets.map((asset) => ({
      id: asset.id,
      type: GalleryItemType.asset,
      item: asset,
    }));

    const selectableFileItems: SelectableGalleryItem[] = files.map((file) => ({
      id: file.id,
      type: GalleryItemType.file,
      item: file,
    }));

    const persist = supportsPersistence || (selectAll && includeNestedItems);

    dispatch(
      setSelectableGalleryItemsAction({
        selectableItems: [...selectableBoardItems, ...selectableAssetItems, ...selectableFileItems],
        persist,
      }),
    );

    // When selectAll is true at root board, select only assets and files (not boards)
    // Don't re-hydrate on navigation - selection persists from root board
    const isAtRootBoard = !selectAllRootBoardId || currentBoardId === selectAllRootBoardId;
    if (selectAll && isAtRootBoard) {
      dispatch(hydrateSelectedItemsAction({ selectedItems: [...selectableAssetItems, ...selectableFileItems] }));
    }
  }, [
    dispatch,
    selectAll,
    includeNestedItems,
    selectAllRootBoardId,
    currentBoardId,
    boards,
    assets,
    files,
    supportsPersistence,
  ]);
}
